/**
 * Analytics Tracking Utility
 * Simple event tracking for key metrics (For Consultants page, conversions, etc.)
 *
 * Usage:
 * import { trackEvent } from '@/utils/analytics'
 * trackEvent('consultant_page_view', { source: 'navigation' })
 */

export interface AnalyticsEvent {
  event: string
  properties?: Record<string, any>
  timestamp?: string
}

export interface PageViewEvent {
  page: string
  path: string
  referrer?: string
  source?: string
}

export interface CTAClickEvent {
  cta_type: 'download_sample' | 'book_demo' | 'get_started' | 'schedule_call'
  page: string
  section?: string
}

export interface ConversionEvent {
  conversion_type: 'payment' | 'onboarding' | 'signup'
  revenue?: number
  plan?: string
}

/**
 * Track a generic event
 * @param eventName - Name of the event
 * @param properties - Event properties/metadata
 */
export function trackEvent(eventName: string, properties?: Record<string, any>): void {
  const event: AnalyticsEvent = {
    event: eventName,
    properties: {
      ...properties,
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
    },
    timestamp: new Date().toISOString(),
  }

  // Log to console in development
  if (import.meta.env.DEV) {
    console.log('[Analytics]', event)
  }

  // Send to analytics service (Google Analytics, Mixpanel, etc.)
  // TODO: Integrate with actual analytics service
  if (typeof window !== 'undefined' && (window as any).gtag) {
    ;(window as any).gtag('event', eventName, properties)
  }

  // Also send to custom endpoint if needed
  sendToAnalyticsEndpoint(event)
}

/**
 * Track page view
 * @param page - Page name
 * @param path - Page path
 * @param metadata - Additional metadata
 */
export function trackPageView(page: string, path: string, metadata?: Record<string, any>): void {
  const event: PageViewEvent = {
    page,
    path,
    referrer: document.referrer,
    ...metadata,
  }

  trackEvent('page_view', event)
}

/**
 * Track CTA click
 * @param ctaType - Type of CTA clicked
 * @param page - Page where CTA was clicked
 * @param section - Section of the page (optional)
 */
export function trackCTAClick(
  ctaType: CTAClickEvent['cta_type'],
  page: string,
  section?: string
): void {
  const event: CTAClickEvent = {
    cta_type: ctaType,
    page,
    section,
  }

  trackEvent('cta_click', event)
}

/**
 * Track conversion event
 * @param conversionType - Type of conversion
 * @param metadata - Conversion metadata (revenue, plan, etc.)
 */
export function trackConversion(
  conversionType: ConversionEvent['conversion_type'],
  metadata?: Omit<ConversionEvent, 'conversion_type'>
): void {
  const event: ConversionEvent = {
    conversion_type: conversionType,
    ...metadata,
  }

  trackEvent('conversion', event)
}

/**
 * Track scroll depth on page
 * @param page - Page name
 * @param depth - Scroll depth percentage (0-100)
 */
export function trackScrollDepth(page: string, depth: number): void {
  trackEvent('scroll_depth', { page, depth })
}

/**
 * Send event to custom analytics endpoint
 * @param event - Analytics event
 */
async function sendToAnalyticsEndpoint(event: AnalyticsEvent): Promise<void> {
  // Skip in development
  if (import.meta.env.DEV) {
    return
  }

  try {
    // TODO: Replace with actual analytics endpoint
    await fetch('/api/analytics', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
      // Use keepalive to ensure request completes even if user navigates away
      keepalive: true,
    })
  } catch (error) {
    // Silent fail - don't block user experience for analytics
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Failed to send event:', error)
    }
  }
}

/**
 * Initialize scroll depth tracking
 * @param page - Page name
 */
export function initScrollDepthTracking(page: string): () => void {
  const depthMilestones = [25, 50, 75, 100]
  const reachedMilestones = new Set<number>()

  const handleScroll = () => {
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollPercentage = Math.round((scrollTop / scrollHeight) * 100)

    depthMilestones.forEach((milestone) => {
      if (scrollPercentage >= milestone && !reachedMilestones.has(milestone)) {
        reachedMilestones.add(milestone)
        trackScrollDepth(page, milestone)
      }
    })
  }

  window.addEventListener('scroll', handleScroll, { passive: true })

  // Cleanup function
  return () => {
    window.removeEventListener('scroll', handleScroll)
  }
}

/**
 * React hook for page view tracking
 * Usage: usePageTracking('For Consultants', '/for-consultants')
 */
export function usePageTracking(page: string, path: string, metadata?: Record<string, any>) {
  if (typeof window !== 'undefined') {
    // Track page view on mount
    trackPageView(page, path, metadata)

    // Initialize scroll depth tracking
    const cleanup = initScrollDepthTracking(page)

    // Return cleanup function
    return cleanup
  }
}

/**
 * Helper to track section visibility (for A/B testing)
 * @param sectionName - Name of the section
 * @param page - Page name
 */
export function trackSectionView(sectionName: string, page: string): void {
  trackEvent('section_view', { section: sectionName, page })
}

/**
 * Weekly metrics structure for simple Google Sheet tracking
 */
export interface WeeklyMetrics {
  week: string // ISO week (e.g., "2024-W43")
  landing_traffic: number
  consultant_traffic: number
  landing_conversion: number
  consultant_conversion: number
  avg_order_value: number
}

/**
 * Export weekly metrics (for admin dashboard)
 */
export function getWeeklyMetrics(): WeeklyMetrics {
  // TODO: Implement actual metrics aggregation
  return {
    week: getISOWeek(),
    landing_traffic: 0,
    consultant_traffic: 0,
    landing_conversion: 0,
    consultant_conversion: 0,
    avg_order_value: 0,
  }
}

/**
 * Get current ISO week string
 */
function getISOWeek(): string {
  const date = new Date()
  const dayNum = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  const weekNum = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
  return `${date.getUTCFullYear()}-W${weekNum.toString().padStart(2, '0')}`
}
